import { useEffect, useState } from 'react';
import type { PaymentDetail as PaymentDetailDto } from '@pet/shared';
import { getPayment, ReceiptApiError } from '../lib/paymentReadClient';
import { formatCentsAsCurrency } from '../lib/money';
import { formatIsoDate } from '../lib/displayDate';
import { attachmentLabel } from '../lib/attachmentLabel';
import TopBar from '../components/TopBar';
import DeletePaymentConfirm from './DeletePaymentConfirm';
import { fixDraftFrom, type FixDraft } from '../lib/fixDraft';
import styles from './Payments.module.css';

interface PaymentDetailProps {
  paymentId: string;
  // The return button's name, which says where it goes: "Back to payments" / "Back to expenses".
  backLabel: string;
  onBack: () => void;
  // After "Delete this payment" succeeds: the host returns to its list and reloads it (AD-19).
  onDeleted: () => void;
  // After "Fix this payment"'s delete succeeds: the app opens the prefilled wizard (AD-19). The
  // button shows only where a host passes it.
  onFix?: (draft: FixDraft, resetIngestionId: string | null) => void;
}

type Screen =
  | { name: 'loading' }
  | { name: 'ready'; payment: PaymentDetailDto }
  | { name: 'error'; message: string };

// HTTP-status -> message mapping is local to the screen, matching the payment wizard's
// mapPostError precedent. A 401 shows a retryable message and never logs the
// user out (AD-06).
function mapGetError(err: unknown): string {
  if (err instanceof ReceiptApiError) {
    switch (err.status) {
      case 401:
        return "Your session couldn't be confirmed. Please try again.";
      case 404:
        return "That payment couldn't be found.";
    }
  }
  return "We couldn't load this payment. Please try again.";
}

export default function PaymentDetail({ paymentId, backLabel, onBack, onDeleted, onFix }: PaymentDetailProps) {
  const [screen, setScreen] = useState<Screen>({ name: 'loading' });
  const [confirming, setConfirming] = useState(false);
  const [fixing, setFixing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setScreen({ name: 'loading' });
    getPayment(paymentId)
      .then((payment) => {
        if (!cancelled) setScreen({ name: 'ready', payment });
      })
      .catch((err) => {
        if (!cancelled) setScreen({ name: 'error', message: mapGetError(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [paymentId]);

  const topBar = <TopBar title="Payment" backLabel={backLabel} onBack={onBack} />;

  if (screen.name === 'loading') {
    return (
      <main className={styles.container}>
        {topBar}
        <p className={styles.statusText} aria-busy="true">
          Loading payment…
        </p>
      </main>
    );
  }

  if (screen.name === 'error') {
    return (
      <main className={styles.container}>
        {topBar}
        <p className={styles.banner} role="alert">
          {screen.message}
        </p>
      </main>
    );
  }

  const p = screen.payment;

  if (fixing && onFix) {
    // Built from the payment already read: no further request before the delete.
    const draft = fixDraftFrom(p);
    return (
      <DeletePaymentConfirm
        payment={p}
        backLabel={backLabel}
        mode="fix"
        carriesOver={draft.memoLost || draft.otherAttachments > 0}
        onKeep={() => setFixing(false)}
        onDeleted={onDeleted}
        onFixed={(resetIngestionId) => onFix(draft, resetIngestionId)}
      />
    );
  }

  if (confirming) {
    return (
      <DeletePaymentConfirm
        payment={p}
        backLabel={backLabel}
        onKeep={() => setConfirming(false)}
        onDeleted={onDeleted}
      />
    );
  }

  // A blank or whitespace-only memo is no note at all.
  const note = p.memo?.trim() ?? '';

  return (
    <main className={styles.container}>
      {topBar}
      <h2 className={styles.heading}>{p.payee}</h2>

      <div className={styles.summaryList}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Date</span>
          <span className={styles.summaryValue}>{formatIsoDate(p.date)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Who paid</span>
          <span className={styles.summaryValue}>
            {p.paymentSource === 'OWNER_FUNDED' ? (p.ownerName ?? 'An owner') : 'The LLC'}
          </span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>How it was paid</span>
          <span className={styles.summaryValue}>{p.paymentMethod ?? 'Not recorded'}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Entered by</span>
          <span className={styles.summaryValue}>{p.enteredByName}</span>
        </div>
        {note !== '' && (
          <div className={`${styles.summaryRow} ${styles.noteRow}`}>
            <span className={styles.summaryLabel}>Note</span>
            <span className={styles.noteValue}>{note}</span>
          </div>
        )}
      </div>

      <div className={styles.summaryTotal}>
        <span>Total</span>
        <span>{formatCentsAsCurrency(p.totalCents)}</span>
      </div>

      <h3 className={styles.sectionHeading}>Expenses in this payment ({p.lineCount})</h3>
      <div className={styles.cardList}>
        {p.lines.map((line) => (
          <div key={line.id} className={styles.lineCard}>
            <span className={styles.lineCardTitle}>
              {formatCentsAsCurrency(line.amountCents)} · {line.propertyAddress}
            </span>
            <span className={styles.lineCardDetail}>Category: {line.taxCategoryLabel}</span>
            <span className={styles.lineCardDetail}>
              {line.deMinimisEligible
                ? 'Marked for small-purchase treatment'
                : 'Not marked for small-purchase treatment'}
            </span>
            {line.description && (
              <span className={styles.lineCardDetail}>{line.description}</span>
            )}
          </div>
        ))}
      </div>

      <h3 className={styles.sectionHeading}>Owner payback</h3>
      {p.reimbursement ? (
        <div className={styles.summaryList}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Owed to</span>
            <span className={styles.summaryValue}>{p.reimbursement.ownerName}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Amount</span>
            <span className={styles.summaryValue}>
              {formatCentsAsCurrency(p.reimbursement.amountCents)}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Status</span>
            <span className={styles.summaryValue}>
              {p.reimbursement.settled ? 'Paid back' : 'Still owed'}
            </span>
          </div>
        </div>
      ) : (
        <p className={styles.emptyNote}>No owner payback — the LLC paid this one.</p>
      )}

      <h3 className={styles.sectionHeading}>Attachments</h3>
      {p.attachments.length > 0 ? (
        <div className={styles.summaryList}>
          {p.attachments.map((a) => (
            <div key={a.id} className={styles.summaryRow}>
              <span className={styles.summaryLabel}>{attachmentLabel(a.kind, a.mime)}</span>
              <span className={styles.summaryValue}>
                {a.hasFile ? 'File on file' : 'No file stored'}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.emptyNote}>No photo or file attached.</p>
      )}

      <div className={styles.actionColumn}>
        {onFix && (
          <button className={styles.secondaryButton} onClick={() => setFixing(true)}>
            Fix this payment
          </button>
        )}
        <button className={styles.dangerButton} onClick={() => setConfirming(true)}>
          Delete this payment
        </button>
      </div>
    </main>
  );
}
